import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { saveAs as saveFile } from 'file-saver';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TransactionStatus } from '../../enums/transaction-status';
import { NewPlanOption } from '../../models/new-plan-option';
import { OwnerInfo } from '../../models/owner-info';
import { SAPContractOptions } from '../../models/sap-contract-options';
import { Transaction } from '../../models/transaction';
import { VehicleDetail } from '../../models/vehicle-detail';
import { DocumentDataService } from '../../services/document/document-data.service';
import { SAPDataService } from '../../services/sap/sap-data.service';
import { SharedStateService } from '../../services/shared-state/shared-state.service';
import { TransactionDataService } from '../../services/transaction/transaction-data.service';

@Component({
  selector: 'app-view-quote',
  templateUrl: './view-quote.component.' + environment.brand + '.html',
  styleUrls: ['./view-quote.component.' + environment.brand + '.css'],
  providers: [SAPDataService, TransactionDataService, DocumentDataService]
})
export class ViewQuoteComponent {
  ownerInfo: OwnerInfo;
  selectedVehicleDetail: VehicleDetail;

  newPlanOption: NewPlanOption;
  acceptConditions = false;
  displayRedirectNotice = false;
  // isPendingContractActivation = false;

  viewQuoteOwnerInfoForm: FormGroup;

  firstNameValidationMessage = '';
  get firstNameInvalid(): boolean { return this.firstNameValidationMessage != '' }

  lastNameValidationMessage = '';
  get lastNameInvalid(): boolean { return this.lastNameValidationMessage != '' }

  mobileNumberValidationMessage = '';
  get mobileNumberInvalid(): boolean { return this.mobileNumberValidationMessage != '' }

  emailAddressValidationMessage = '';
  get emailAddressInvalid(): boolean { return this.emailAddressValidationMessage != '' }

  constructor(private sharedStateService: SharedStateService,
    private sapDataService: SAPDataService,
    private transactionDataService: TransactionDataService,
    private documentDataService: DocumentDataService,
    private formBuilder: FormBuilder) {
    //console.log('ViewQuoteComponent.constructor');
    if (!this.sharedStateService.ownerInfo) {
      this.sharedStateService.showStart();
      return;
    }

    // get data from shared state service
    this.ownerInfo = this.sharedStateService.ownerInfo;
    this.selectedVehicleDetail = this.sharedStateService.selectedVehicleDetail;

    // set new plan options
    this.newPlanOption = sharedStateService.newPlanOption;

    // create form group for editing owner info
    this.createFormGroup();

    // populate form values
    this.viewQuoteOwnerInfoForm.controls['idNumber'].setValue(this.ownerInfo.idNumber);
    this.viewQuoteOwnerInfoForm.controls['firstName'].setValue(this.ownerInfo.firstName);
    this.viewQuoteOwnerInfoForm.controls['lastName'].setValue(this.ownerInfo.lastName);
    this.viewQuoteOwnerInfoForm.controls['mobileNumber'].setValue(this.ownerInfo.mobileNumber);
    this.viewQuoteOwnerInfoForm.controls['emailAddress'].setValue(this.ownerInfo.email);

    if (!this.ownerInfo.email) {
      this.onValidateInput('emailAddress', this.ownerInfo.email);
    }

    // this.transactionDataService.checkForPendingContractActivation(this.selectedVehicleDetail.vin).subscribe(result => {
    //   this.isPendingContractActivation = result;
    // })
  }

  createFormGroup(): void {
    this.viewQuoteOwnerInfoForm = this.formBuilder.group({
      idNumber: new FormControl('', [Validators.required]),
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      mobileNumber: new FormControl('', [Validators.pattern('^(\\+27|27|0)[0-9]{9}')]),
      emailAddress: new FormControl('', [Validators.required, Validators.email])
    });
  }

  onValidateInput(controlName: string, value: any): void {
    if (value != null) {
      this.viewQuoteOwnerInfoForm.controls[controlName].setValue(value.trimStart());
      //console.log('[' + this.viewQuoteOwnerInfoForm.controls[controlName].value + ']');
    }

    if (controlName == 'firstName') {
      if (!this.viewQuoteOwnerInfoForm.controls[controlName].errors) {
        this.firstNameValidationMessage = '';
      } else {
        this.firstNameValidationMessage = 'Please enter your first name';
      }
    }

    if (controlName == 'lastName') {
      if (!this.viewQuoteOwnerInfoForm.controls[controlName].errors) {
        this.lastNameValidationMessage = '';
      } else {
        this.lastNameValidationMessage = 'Please enter your last name';
      }
    }

    if (controlName == 'mobileNumber') {
      if (!this.viewQuoteOwnerInfoForm.controls[controlName].errors) {
        this.mobileNumberValidationMessage = '';
      } else if (this.viewQuoteOwnerInfoForm.controls[controlName].errors.required) {
        this.mobileNumberValidationMessage = 'Please enter your mobile number';
      } else if (this.viewQuoteOwnerInfoForm.controls[controlName].errors.pattern) {
        this.mobileNumberValidationMessage = 'Please enter a valid mobile number';
      }
    }

    if (controlName == 'emailAddress') {
      if (!this.viewQuoteOwnerInfoForm.controls[controlName].errors) {
        this.emailAddressValidationMessage = '';
      } else if (this.viewQuoteOwnerInfoForm.controls[controlName].errors.required) {
        this.emailAddressValidationMessage = 'Please enter your email address';
      } else if (this.viewQuoteOwnerInfoForm.controls[controlName].errors.email) {
        this.emailAddressValidationMessage = 'Please enter a valid email address';
      }
    }
  }

  onViewTermsAndConditions(): void {
    // download the pdf
    this.sharedStateService.toggleLoader(true);
    this.documentDataService.getConditionsPdf(environment.brand, 'TermsAndConditions', this.newPlanOption.planType)
      .pipe(finalize(() => { this.sharedStateService.toggleLoader(false); }))
      .subscribe(contentDocuments => {
        //console.log('onViewTermsAndConditions', JSON.stringify(contentDocuments));
        const fileName = (environment.brand == 'vwsa' ? 'EasyDrive' : 'Audi Freeway Plan') + ' Terms and Conditions.pdf'
        const file = this.base64ToFile(contentDocuments[0].contentDocumentValue, fileName, 'pdf');
        saveFile(file);
      }, error => {
        //console.log('onViewTermsAndConditions', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error getting Terms and Conditions',
          message: 'We could not get the Terms and Conditions.'
        });
      });
  }

  base64ToFile(b64Data: string, filename: string, contentType: string): File {
    const sliceSize = 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const file = new File(byteArrays, filename, { type: contentType });
    return file;
  }

  onDownloadQuote(): void {
    // init the transaction object
    const transaction = this.initTransaction('');

    // download the pdf
    this.sharedStateService.toggleLoader(true);
    this.documentDataService.getQuotePdf(transaction)
      .pipe(finalize(() => { this.sharedStateService.toggleLoader(false); }))
      .subscribe(document => {
        const plan = environment.brand == 'vwsa' ? 'EasyDrive' : 'Freeway Plan';
        saveFile(document, plan + ' Quote ' + new Date().toISOString().split('T')[0] + '.pdf');
      }, error => {
        //console.log('onDownloadQuote', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error downloading quote',
          message: 'Please contact us for assistance.'
        });
      });
  }

  onBuyNow(): void {
    // show the redirect notice
    this.displayRedirectNotice = this.acceptConditions && this.viewQuoteOwnerInfoForm.valid;
    // this.displayRedirectNotice = this.acceptConditions && this.viewQuoteOwnerInfoForm.valid && this.isPendingContractActivation;
  }

  onProceedToPayment(): void {
    // user has accepted the redirect notice
    this.displayRedirectNotice = false;
    this.createSAPContract();
    window.scroll(0, 0);
  }

  createSAPContract(): void {
    // create the contract in SAP, then the PayU transaction
    const contract = new SAPContractOptions();
    contract.vin = this.selectedVehicleDetail.vin;
    contract.idNumber = this.viewQuoteOwnerInfoForm.value.idNumber;
    //contract.partnerGuid = this.ownerInfo.partnerGuid;
    contract.firstName = this.viewQuoteOwnerInfoForm.value.firstName;
    contract.lastName = this.viewQuoteOwnerInfoForm.value.lastName;
    contract.mobileNumber = this.viewQuoteOwnerInfoForm.value.mobileNumber;
    contract.emailAddress = this.viewQuoteOwnerInfoForm.value.emailAddress;
    contract.salesDocType = this.newPlanOption.salesDocType;
    contract.currentOdometer = this.selectedVehicleDetail.currentOdometer;
    contract.startKM = this.newPlanOption.planStartKM;
    contract.endKM = this.newPlanOption.maxKilometers;
    contract.years = this.newPlanOption.years;

    this.sharedStateService.toggleLoader(true);
    this.sapDataService.createContract(contract)
      .pipe(finalize(() => { this.sharedStateService.toggleLoader(false); }))
      .subscribe(contract => {
        this.createTransaction(contract.contractNumber);
      }, error => {
        //console.log('createSAPContract', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error creating contract',
          message: 'Please contact us for assistance.'
        });
      });
  }

  createTransaction(sapReference: string): void {
    // creates the transaction entry in the db and the PayU transaction
    const transaction = this.initTransaction(sapReference);

    this.sharedStateService.toggleLoader(true);
    this.transactionDataService.createTransaction(transaction)
      .subscribe(transaction => {
        //console.log(JSON.stringify(transaction));
        if (transaction.transactionStatusId < TransactionStatus.Successful) {
          // save state for when PayU redirects the user back
          this.sharedStateService.saveState();
          // redirect to PayU payment page
          window.location.assign(environment.paymentUrl + '?PayUReference=' + transaction.payUReference);
        } else {
          this.sharedStateService.toggleLoader(false);
          this.sharedStateService.showDialog({
            isVisible: true,
            header: 'We could not create the transaction',
            message: 'Please try again or contact us for assistance.'
          });
        }
      }, error => {
        this.sharedStateService.toggleLoader(false);
        //console.log('createTransaction', error);
        this.sharedStateService.showDialog({
          isVisible: true,
          header: 'Error creating transaction',
          message: 'Please contact us for assistance.'
        });
      });
  }

  private initTransaction(sapReference: string): Transaction {
    const transaction = new Transaction();
    transaction.idNumber = this.sharedStateService.ownerInfo.idNumber; //Test this with PayU
    transaction.vin = this.sharedStateService.selectedVehicleDetail.vin;
    transaction.brandName = environment.brand;
    transaction.brandCode = this.sharedStateService.selectedVehicleDetail.brandCode;
    transaction.odometerReadingKm = this.selectedVehicleDetail.currentOdometer;
    transaction.newPlanType = this.newPlanOption.planType;
    transaction.newPlanYears = this.newPlanOption.years;
    transaction.newPlanDistanceKm = this.newPlanOption.maxKilometers;
    transaction.newPlanTotalIncludingVat = this.newPlanOption.price;
    transaction.firstName = this.viewQuoteOwnerInfoForm.value.firstName;
    transaction.lastName = this.viewQuoteOwnerInfoForm.value.lastName;
    transaction.mobileNumber = this.viewQuoteOwnerInfoForm.value.mobileNumber;
    transaction.emailAddress = this.viewQuoteOwnerInfoForm.value.emailAddress;
    transaction.sapContractNumber = sapReference;
    transaction.isHappyToBeContacted = true; // was linked to download quote
    transaction.isAcceptedTermsAndConditions = true;
    transaction.salesDocType = this.newPlanOption.salesDocType;
    transaction.isOwnerDetailsUpdated = this.checkIfOwnerDetailsUpdated();
    transaction.payUReference = '';
    transaction.isCustomPlan = this.newPlanOption.isCustomPlan;

    // set additional properties
    transaction.modelCode = this.selectedVehicleDetail.modelNumber;
    transaction.modelName = this.selectedVehicleDetail.modelName;
    transaction.modelDescription = this.selectedVehicleDetail.modelDescription;
    transaction.engineNumber = this.selectedVehicleDetail.engineNumber;
    transaction.deliveryDate = this.selectedVehicleDetail.planStart;
    transaction.currentPlanType = this.selectedVehicleDetail.planType;
    transaction.currentPlanStartDate = this.selectedVehicleDetail.planStart;
    transaction.currentPlanEndDate = this.selectedVehicleDetail.planEnd;
    transaction.currentPlanStartKms = this.selectedVehicleDetail.startKM;
    transaction.currentPlanEndKms = this.selectedVehicleDetail.currentPlanEndKM;
    transaction.newPlanStartDate = this.newPlanOption.planStartDate;
    transaction.newPlanEndDate = this.newPlanOption.planEndDate;
    transaction.newPlanStartKms = this.newPlanOption.planStartKM;
    transaction.newPlanEndKms = this.newPlanOption.maxKilometers;
    transaction.warrantyStatus = this.selectedVehicleDetail.warrantyStatus;
    transaction.warrantyStartDate = this.selectedVehicleDetail.warrantyStartDate;
    transaction.warrantyEndDate = this.selectedVehicleDetail.warrantyEndDate;
    transaction.warrantyEndKM = this.selectedVehicleDetail.warrantyEndKM;
    transaction.returnUrl = window.location.href.substring(0, window.location.href.lastIndexOf('/')) + '/payment';

    //console.log(JSON.stringify(transaction));
    return transaction;
  }

  onBackToCustomizePlan(): void {
    this.sharedStateService.showSelectPlan();
  }

  checkIfOwnerDetailsUpdated(): boolean {
    return this.sharedStateService.ownerInfo.firstName != this.viewQuoteOwnerInfoForm.value.firstName ||
      this.sharedStateService.ownerInfo.lastName != this.viewQuoteOwnerInfoForm.value.lastName ||
      this.sharedStateService.ownerInfo.mobileNumber != this.viewQuoteOwnerInfoForm.value.mobileNumber ||
      this.sharedStateService.ownerInfo.email != this.viewQuoteOwnerInfoForm.value.emailAddress;
  }
}
